/**
 * product-live-qty.js — Live cart-quantity stepper for the product detail page.
 *
 * Makes the PDP quantity control behave like the collection card's bulk stepper:
 *   - the input shows the CURRENT cart quantity for the variant (not a default of 1),
 *   - +/- and typing update the cart line live (debounced), via the same /cart/update.js
 *     bulk endpoint the cards use — so the black bar total + $200 gate update automatically,
 *   - reaching 0 removes the line,
 *   - the (now redundant) Add-to-cart button is hidden.
 *
 * Gracefully degrades: with JS disabled the PDP keeps its normal Add-to-cart flow. This script
 * only takes over once it successfully binds (it adds body.pdp-live-qty-on, which the section's
 * CSS uses to hide the Add-to-cart button + the native "(N in cart)" label).
 *
 * Isolated from the collection-card path (quick-add-bulk.js / green-cart-update-bymark.js) on
 * purpose — no shared code is touched.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 500;
  var WRAP_SELECTOR = '[data-pdp-live-qty]';

  function isSpanish() {
    return window.location.pathname.indexOf('/es') === 0;
  }

  function greenText(qty) {
    if (isSpanish()) {
      return '⬤ ' + qty + ' ' + (qty === 1 ? 'pza' : 'pzas') + ' en su carrito';
    }
    return '⬤ ' + qty + ' ' + (qty === 1 ? 'pc' : 'pcs') + ' in your cart';
  }

  function getWrap() {
    return document.querySelector(WRAP_SELECTOR);
  }

  function setMessage(wrap, qty) {
    var msg = wrap.querySelector('.pdp-cart-qty-message');
    if (!msg) return;
    if (qty > 0) {
      msg.textContent = greenText(qty);
      msg.hidden = false;
    } else {
      msg.textContent = '';
      msg.hidden = true;
    }
  }

  // Cart drawer + header count so the rest of the theme stays in sync after a live update.
  function sectionsToRender() {
    return [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' }
    ];
  }

  function innerHTMLFrom(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  // --- serialized, debounced write path ---------------------------------------
  var busy = false;
  var pending = null; // latest quantity requested while a write was in flight
  var timer = null;

  function commit(wrap, qty) {
    var variantId = parseInt(wrap.dataset.variantId, 10);
    if (!variantId) return Promise.resolve();
    busy = true;

    var toRender = sectionsToRender();
    var updates = {};
    updates[variantId] = qty;
    var body = JSON.stringify({
      updates: updates,
      sections: toRender.map(function (s) { return s.section; }),
      sections_url: window.location.pathname
    });
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        // Refresh header cart bubble + drawer from the returned section HTML.
        toRender.forEach(function (s) {
          if (!state.sections || !state.sections[s.section]) return;
          var host = document.getElementById(s.id);
          if (!host) return;
          var target = host.querySelector(s.selector) || host;
          var html = innerHTMLFrom(state.sections[s.section], s.selector);
          if (html != null) target.innerHTML = html;
        });
        setMessage(wrap, qty);
        // Let Dawn components + our black bar / gate know the cart changed.
        try {
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-live-qty', cartData: state });
          }
        } catch (e) { /* no-op */ }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function (e) { console.error('[pdp-live-qty]', e); })
      .finally(function () {
        busy = false;
        if (pending != null) {
          var next = pending;
          pending = null;
          schedule(wrap, next, 0);
        }
      });
  }

  function schedule(wrap, qty, delay) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (busy) { pending = qty; return; }
      commit(wrap, qty);
    }, delay == null ? DEBOUNCE_MS : delay);
  }

  function readQty(input) {
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 0) v = 0;
    return v;
  }

  function hydrate(wrap) {
    var input = wrap.querySelector('input.quantity__input');
    if (!input) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var variantId = parseInt(wrap.dataset.variantId, 10);
        var qty = 0;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].id === variantId || cart.items[i].variant_id === variantId) {
            qty = cart.items[i].quantity;
            break;
          }
        }
        if (document.activeElement !== input) input.value = qty;
        input.dataset.cartQuantity = qty;
        setMessage(wrap, qty);
      })
      .catch(function () { /* no-op */ });
  }

  function enable(wrap) {
    if (wrap.dataset.pdpLiveReady) return;
    wrap.dataset.pdpLiveReady = '1';

    var input = wrap.querySelector('input.quantity__input');
    if (!input) return;

    // Detach from the add-to-cart form (so Enter can't submit it) and allow reaching 0.
    input.removeAttribute('form');
    input.setAttribute('min', '0');
    input.min = '0';

    // Switch the page into live mode (CSS hides the add-to-cart button + native "(N in cart)").
    document.body.classList.add('pdp-live-qty-on');

    hydrate(wrap);

    input.addEventListener('change', function () {
      var qty = readQty(input);
      setMessage(wrap, qty); // optimistic
      schedule(wrap, qty);   // debounced, serialized commit
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    });
  }

  function init() {
    var wrap = getWrap();
    if (wrap) enable(wrap);

    // Re-bind after Dawn re-renders the product section (e.g. variant switch).
    document.addEventListener('shopify:section:load', function () {
      var w = getWrap();
      if (w) enable(w);
    });

    // Stay in sync if the cart is changed elsewhere on the page (e.g. from the drawer).
    document.addEventListener('cart:refresh', function () {
      if (busy) return;
      var w = getWrap();
      if (w) hydrate(w);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
